import { useEffect, useRef, useState } from "react";
import {
  getConversation, listMessages, sendMessage, markRead, sendTyping, setPresence,
} from "../../lib/messagingApi";
import { useUnread } from "../../context/UnreadContext";
import { useMessagingStream } from "../../context/MessagingStreamContext";
import { getCachedRoom, setCachedRoom, appendCachedMessage, replaceCachedMessage, removeCachedMessage } from "../../lib/roomCache";
import { invalidateConversations } from "../../lib/conversationsStore";

// Owns all of the room's data lifecycle: fetch conversation + messages,
// subscribe to the shared SSE stream, presence heartbeat, send + typing.
// Returns state + handlers ready to be wired into the JSX.
export function useRoomData({ convId, myId, scrollToEnd }) {
  const { refresh: refreshUnread } = useUnread();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [otherTyping, setOtherTyping] = useState(false);
  const [otherSeenMsgId, setOtherSeenMsgId] = useState("");
  const [otherSeenAt, setOtherSeenAt] = useState("");
  const typingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  const applyReads = (reads) => {
    if (!reads) return;
    const other = Object.values(reads).find((r) => r?.last_read_message_id);
    if (other?.last_read_message_id) setOtherSeenMsgId(other.last_read_message_id);
    if (other?.last_read_at) setOtherSeenAt(other.last_read_at);
  };

  const load = async () => {
    // Instant paint from cache if available — silent revalidation follows.
    const cached = getCachedRoom(convId);
    if (cached) {
      if (cached.conv) setConv(cached.conv);
      if (cached.messages?.length) setMessages(cached.messages);
      applyReads(cached.reads);
      setLoading(false);
      setTimeout(() => scrollToEnd(false), 30);
    } else {
      setLoading(true);
    }
    try {
      // Parallelize the two independent GETs — was previously sequential.
      const [c, list] = await Promise.all([
        getConversation(convId),
        listMessages(convId, { limit: 80 }),
      ]);
      const { messages: msgs, reads } = list;
      setConv(c);
      setMessages(msgs);
      applyReads(reads);
      setCachedRoom(convId, { conv: c, messages: msgs, reads });
      const last = msgs[msgs.length - 1];
      if (last && last.sender_id !== myId) {
        await markRead(convId, last.id).catch(() => {});
        refreshUnread();
        // Invalidate the /messages list cache too. SSE `conversation:read`
        // fires only for OTHER mounted MessagesList tabs — the current
        // one is unmounted (we're inside the room), so the badge on the
        // row would linger for up to 30 s (staleMs). One extra call
        // here makes the badge clear the moment the user navigates back.
        invalidateConversations();
      }
      // Always jump to newest after fresh fetch. Previously this only
      // fired when there was no cache, so if 1 new msg arrived while
      // the user was away, the cache paint scrolled to (old) bottom,
      // then the fetch appended the new msg BELOW the fold — invisible
      // until the user scrolled. Two schedules give the DOM time to
      // insert the new bubble before we measure `scrollHeight`.
      setTimeout(() => scrollToEnd(false), 30);
      setTimeout(() => scrollToEnd(false), 120);
    } catch (err) {
      // The axios interceptor already toasts on 401/402/403 with the
      // Arabic backend detail. Swallow those here so they don't bubble
      // up as an unhandled promise rejection (which would show the CRA
      // red overlay in dev and flood Sentry in prod). Re-throw anything
      // else so genuine server errors are still surfaced.
      const status = err?.response?.status;
      if (![401, 402, 403].includes(status)) throw err;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!convId) return;
    load();
  }, [convId, myId]);

  useMessagingStream((ev) => {
    if (!ev || !convId || ev.conversation_id !== convId) return;
    if (ev.type === "message:new") {
      setMessages((m) => (m.some((x) => x.id === ev.message.id) ? m : [...m, ev.message]));
      appendCachedMessage(convId, ev.message);
      if (ev.message.sender_id !== myId) {
        markRead(convId, ev.message.id).catch(() => {});
        refreshUnread();
        setOtherTyping(false);
      }
      // Keep the /messages list cache in sync: the incoming message
      // shifts `last_message_preview` + `last_message_at` for the row,
      // and (if peer-sent) the row's `unread` was just cleared by the
      // markRead above.  Without this the user sees a stale preview
      // when they navigate back.
      invalidateConversations();
      setTimeout(() => scrollToEnd(true), 30);
    } else if (ev.type === "conversation:typing" && ev.user_id !== myId) {
      setOtherTyping(!!ev.is_typing);
      if (ev.is_typing) setTimeout(() => setOtherTyping(false), 4500);
    } else if (ev.type === "conversation:read-by" && ev.user_id !== myId) {
      setOtherSeenMsgId(ev.last_read_message_id);
      setOtherSeenAt(new Date().toISOString());
    }
  });

  // Presence heartbeat — skips push notifications when both parties are in
  // the same thread. Beats every 20s, drops on unmount + on tab-hidden.
  useEffect(() => {
    if (!convId) return undefined;
    setPresence(convId);
    const hb = setInterval(() => setPresence(convId), 20_000);
    const onVis = () => setPresence(document.hidden ? null : convId);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(hb);
      document.removeEventListener("visibilitychange", onVis);
      setPresence(null);
    };
  }, [convId]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const body = (draft || "").trim();
    if (!body) return;
    // Optimistic UI: paint the bubble + clear the composer INSTANTLY
    // (~5 ms) instead of waiting for the server round-trip + fan-out
    // (~150-400 ms). The user perception is "iMessage-fast" even on 4G.
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      id: tempId,
      _pending: true,
      conversation_id: convId,
      sender_id: myId,
      body,
      attachment: null,
      created_at: new Date().toISOString(),
    };
    setDraft("");
    setMessages((m) => [...m, optimistic]);
    appendCachedMessage(convId, optimistic);
    setTimeout(() => scrollToEnd(true), 30);
    try {
      const saved = await sendMessage(convId, { body });
      // Swap the tmp bubble for the canonical one IN-PLACE so rapid-fire
      // sends preserve their tap order even when responses resolve out
      // of order. If SSE already delivered the canonical id, we simply
      // drop the temp copy.
      setMessages((m) => {
        const idx = m.findIndex((x) => x.id === tempId);
        const hasCanonical = m.some((x) => x.id === saved.id);
        if (idx === -1) return hasCanonical ? m : [...m, saved];
        if (hasCanonical) return m.filter((x) => x.id !== tempId);
        const next = [...m];
        next[idx] = saved;
        return next;
      });
      replaceCachedMessage(convId, tempId, saved);
      // Sync the /messages row's `last_message_preview` + `last_sender_id`
      // so returning to the list shows the just-sent message immediately.
      invalidateConversations();
    } catch {
      // Roll back — remove the optimistic bubble, restore the draft so
      // the user can retry with one tap.
      setMessages((m) => m.filter((x) => x.id !== tempId));
      removeCachedMessage(convId, tempId);
      setDraft(body);
    }
  };

  const handleDraftChange = (val) => {
    setDraft(val);
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      lastTypingSentRef.current = now;
      sendTyping(convId, true).catch(() => {});
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      sendTyping(convId, false).catch(() => {});
    }, 2500);
  };

  return {
    conv, messages, loading, sending: false, draft,
    otherTyping, otherSeenMsgId, otherSeenAt,
    setDraft, handleSend, handleDraftChange,
  };
}
